import { type Building } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { CanAccess, DataList, Typography } from "@eisox/design-system";
import { isFeatureSupported } from "@eisox/device-features";
import { HistoryIcon, UpdateIcon } from "@eisox/icons";
import { useMediaQuery } from "@eisox/tools";
import { calcRodStroke, valveVersionHandler } from "@eisox/valves";

import { Help } from "~/components/help";
import { ConnectivityHistoryDialog } from "~/features/equipment";
import { formatTrigCurThreshold, getValveBatteryStatus } from "~/helpers/valve.helper";
import { m } from "~/paraglide/messages";
import type { Permission } from "~/types/auth.type";
import type { EnhancedValve } from "~/types/valve.type";

import { getValveProblemTranslation } from "../helpers";
import { BatteryHistoryDialog } from "./battery-history-dialog";
import { MecaHistoryDialog } from "./meca-history-dialog";
import { RelaunchLearningDialog } from "./relaunch-learning-dialog";
import { ValveHistoryDialog } from "./valve-history-dialog";

interface ValveDetailContentProps {
  valve: EnhancedValve;
  building: Building;
  boilerRooms: Array<{ id: string; name: string }>;
  heatingNetworks: Array<{ id: string; name: string; boilerRoomId: string }>;
  valves: EnhancedValve[];
  permissions: Permission;
  canSeeInstallerWarnings: boolean;
  interactive?: boolean;
}

export const ValveDetailContent: React.FC<ValveDetailContentProps> = ({
  valve,
  building,
  boilerRooms,
  heatingNetworks,
  valves,
  permissions,
  canSeeInstallerWarnings,
  interactive = false,
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const translation = getValveProblemTranslation(valve);
  const batteryStatus = getValveBatteryStatus(valve.stateData ?? {}, permissions, canSeeInstallerWarnings);
  const heatingNetwork = heatingNetworks.find(hn => hn.id === valve.heatingNetworkId);
  const boilerRoom = boilerRooms.find(b => b.id === heatingNetwork?.boilerRoomId);

  return (
    <>
      <DataList.Root className="md:gap-y-5 gap-x-2.5 gap-y-2.5 grid max-h-fit grid-cols-2" orientation="vertical">
        <DataList.Item>
          <DataList.Label>{m.index()}</DataList.Label>
          <DataList.Value>{valve.uid}</DataList.Value>
        </DataList.Item>
        <DataList.Item>
          <DataList.Label>{m.mac()}</DataList.Label>
          <DataList.Value>{valve.mac ?? "-"}</DataList.Value>
        </DataList.Item>
        <DataList.Item>
          <DataList.Label>{m.gateway_name()}</DataList.Label>
          <DataList.Value>{valve.gatewayName}</DataList.Value>
        </DataList.Item>
        <DataList.Item>
          <DataList.Label>{m.room()}</DataList.Label>
          <DataList.Value>{valve.roomName || "-"}</DataList.Value>
        </DataList.Item>
        <DataList.Item>
          <DataList.Label>{m.version()}</DataList.Label>
          <DataList.Value>
            {valveVersionHandler(valve.hardwareVersion ?? "-")} ({valve.softwareVersion ?? "-"})
          </DataList.Value>
        </DataList.Item>
        <DataList.Item>
          <DataList.Label>{m.button_settings()}</DataList.Label>
          <DataList.Value>
            {`${valve.stateData?.swOffset !== undefined ? `${valve.stateData.swOffset > 0 ? "+" : ""}${valve.stateData.swOffset}` : "-"}°C`}
          </DataList.Value>
        </DataList.Item>
        <DataList.Item>
          <DataList.Label>{m.signal_level()}</DataList.Label>
          <DataList.Value>
            {valve.rssi ?? "-"} dBm (LQI: {valve.lqi ?? "-"}/255)
          </DataList.Value>
        </DataList.Item>
        <DataList.Item>
          <DataList.Label className="gap-1.25 flex items-center">
            {m.rod_stroke()}
            {interactive && !isMobile && valve.mac && permissions.history?.valve.meca?.read && (
              <MecaHistoryDialog buildingId={building.id} valve={valve}>
                <HistoryIcon className="size-4 cursor-pointer text-primary-dark-gray" />
              </MecaHistoryDialog>
            )}
            {interactive && valve.mac && valve.gatewayMac && (
              <RelaunchLearningDialog buildingId={building.id} valve={valve}>
                <UpdateIcon className="size-4 cursor-pointer text-primary-dark-gray" />
              </RelaunchLearningDialog>
            )}
          </DataList.Label>
          <DataList.Value className="flex-col">
            <p>
              {valve.stateData?.openPos !== undefined && valve.stateData.closePos !== undefined
                ? `${calcRodStroke(valve.stateData.openPos, valve.stateData.closePos)} mm (${valve.stateData.openPos}-${valve.stateData.closePos}/1630)`
                : "-"}
            </p>
            {valve.stateData?.posUpdatedAt && <p>{dayjs(valve.stateData.posUpdatedAt).format("L - LT")}</p>}
            <CanAccess
              isAuthorized={
                !!permissions.valve?.trigCur?.read && isFeatureSupported("valve", "TRIG_CUR", valve.softwareVersion)
              }
            >
              <p>{m.trig_cur_threshold({ value: formatTrigCurThreshold(valve.trigCur) })}</p>
            </CanAccess>
          </DataList.Value>
        </DataList.Item>
        <DataList.Item>
          <DataList.Label>{m.opening()}</DataList.Label>
          <DataList.Value>{valve.closing !== undefined ? `${100 - valve.closing}%` : "-"}</DataList.Value>
        </DataList.Item>
        <CanAccess isAuthorized={permissions.valve?.heatingNetworkId?.read && building.pages.boilerRooms}>
          {heatingNetwork && (
            <Typography>
              <DataList.Item>
                <DataList.Label>{m.network()}</DataList.Label>
                <DataList.Value>
                  {boilerRoom ? `${boilerRoom.name}/` : ""}
                  {heatingNetwork.name}
                </DataList.Value>
              </DataList.Item>
            </Typography>
          )}
        </CanAccess>
        <CanAccess isAuthorized={permissions.valve?.stateData?.temperatureCorrectionCoef?.read}>
          <DataList.Item>
            <DataList.Label>
              {m.calibration()}
              <Help backgroundColor="white">{m.calibration_accounts_for_radiator_heat()}</Help>
            </DataList.Label>
            <DataList.Value>X{valve.stateData?.temperatureCorrectionCoef}</DataList.Value>
          </DataList.Item>
        </CanAccess>
        <DataList.Item className="col-span-full flex flex-col">
          <DataList.Label className="gap-1.25 flex items-center">
            {m.battery()}
            {interactive && !isMobile && valve.mac && permissions.history?.valve.battery?.read && (
              <BatteryHistoryDialog buildingId={building.id} valve={valve}>
                <HistoryIcon className="size-4 cursor-pointer text-primary-dark-gray" />
              </BatteryHistoryDialog>
            )}
          </DataList.Label>
          <DataList.Value className="col-span-full flex flex-col">
            {batteryStatus.status && <p>{batteryStatus.status}</p>}
            {batteryStatus.level && <p>{batteryStatus.level}</p>}
            {batteryStatus.changed && <p>{batteryStatus.changed}</p>}
          </DataList.Value>
        </DataList.Item>
        <CanAccess isAuthorized={permissions.history?.valve.histoValve?.read && !!valve.mac}>
          <DataList.Item className="col-span-full">
            <DataList.Label>{m.data_history()}</DataList.Label>
            <DataList.Value>
              {interactive ? (
                <ValveHistoryDialog key={valve.id} buildingId={building.id} valves={valves} valveId={valve.id}>
                  <button type="button" className="gap-1.25 flex cursor-pointer items-center">
                    {m.consult()}
                    <HistoryIcon className="shrink-0" />
                  </button>
                </ValveHistoryDialog>
              ) : (
                "-"
              )}
            </DataList.Value>
          </DataList.Item>
        </CanAccess>
        <DataList.Item className="col-span-full flex flex-col">
          <DataList.Label className="gap-1.25 flex items-center">
            {m.last_communication_date()}
            {interactive && !isMobile && valve.mac && permissions.history?.valve.lastActivity?.read && (
              <ConnectivityHistoryDialog
                buildingId={building.id}
                equipmentId={valve.id}
                label={`${valve.uid}`}
                mac={valve.mac}
              >
                <HistoryIcon className="size-4 cursor-pointer text-primary-dark-gray" />
              </ConnectivityHistoryDialog>
            )}
          </DataList.Label>
          <DataList.Value>{valve.updatedAt ? dayjs(valve.updatedAt).format("L - LT") : "-"}</DataList.Value>
        </DataList.Item>
      </DataList.Root>
      {translation.errors.length > 0 && (
        <div className="gap-1.25 flex flex-col text-base text-primary-red">
          {translation.errors.map(error => (
            <p key={error}>{error}</p>
          ))}
        </div>
      )}
      {translation.warnings.length > 0 && (
        <div className="gap-1.25 flex flex-col text-base text-primary-orange">
          {translation.warnings.map(warning => (
            <p key={warning}>{warning}</p>
          ))}
        </div>
      )}
    </>
  );
};

ValveDetailContent.displayName = "ValveDetailContent";
